import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GlobalHotKeys } from 'react-hotkeys';

import { Paper } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { RootState } from 'state/store';
import { selectLeadFullName } from 'state/lead.state';
import { apiInitiateCallCenterFlow, setOutboundRinging } from 'state/call.state';
import { AppCopyText, CallTypes, Colors } from 'typings';

import OnCall from './CallPanel/OnCall';
import CallLead from './CallPanel/CallLead';
import AnswerCall from './CallPanel/AnswerCall';
import InboundCallGreeting from './CallPanel/InboundCallGreeting';
import ActionsPanel from './ActionsPanel';
import {
  isCallCenterTransferRestricted,
  isValidPhoneNumber,
  isWithinBusinessHours,
  isTeamHoliday,
} from 'utils/functions';
import { getCommunicationService } from 'services/communication/communication.service.factory';
import { setNotification } from 'state/app.state';

const inboundRingingSrc = require('assets/sounds/inbound-ringing.mp3');
const outboundRingingSrc = require('assets/sounds/outbound-ringing.mp3');

const getPanelBackground = (props: { [key: string]: any }, theme: Theme) => {
  if (props.isReceivingInboundCall) {
    return `transparent linear-gradient(90deg, #F24689 0%, #4397ED 100%) 0% 0% no-repeat padding-box`;
  }
  if (props.isOnCall) {
    return `transparent linear-gradient(90deg, ${theme.palette.background.green} 0%, ${theme.palette.primary.main} 100%) 0% 0% no-repeat padding-box`;
  }
  if (!props.hasCallAbility) {
    return Colors.LIGHT_GRAY;
  }
  if (props.isCallCenterRoutingEnabled) {
    return Colors.PURPLE;
  }

  return theme.palette.primary.main;
};

const useStyles = makeStyles((theme: Theme) => ({
  callPanelWrapper: (props: any) => ({
    position: 'relative',
    color: Colors.WHITE,
    background: getPanelBackground(props, theme),
    padding: theme.spacing(1.5),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  }),
}));

const LeadActionsPanel = () => {
  const dispatch = useDispatch();
  const inboundRingingRef = useRef<HTMLAudioElement | null>(null);
  const outboundRingingRef = useRef<HTMLAudioElement | null>(null);
  const lead = useSelector((state: RootState) => state.lead.entity);
  const concierge = useSelector((state: RootState) => state.concierge.entity);
  const leadName = useSelector(selectLeadFullName);
  const isOnCall = useSelector((state: RootState) => state.concierge.isOnCall);
  const isInboundCall = useSelector(
    (state: RootState) => state.call.callType === CallTypes.INBOUND
  );
  const isReceivingInboundCall = useSelector((state: RootState) => state.call.incomingCall);
  const isCallCenterRoutingEnabled = useSelector(
    (state: RootState) =>
      !!state.lead.entity?.agent?.agentSetting?.settings?.inboundCalls?.callCenterRouting?.enabled
  );

  const [isAnswering, setIsAnswering] = useState(false);
  const [isDialing, setIsDialing] = useState(false);
  const isFetchingCallToken = useSelector(
    (state: RootState) => state.concierge.isFetchingCallToken
  );
  const outboundRinging = useSelector((state: RootState) => state.call.outboundRinging);

  const isEngaged = isOnCall || isReceivingInboundCall;
  const showGreeting = (isOnCall && isInboundCall) || isReceivingInboundCall;
  const hasCallAbility =
    isCallCenterRoutingEnabled || (lead?.communicationProvider && !!concierge?.canCall);
  const canMakeCallCenterCall =
    lead?.agent &&
    (!isCallCenterTransferRestricted(lead.agent) ||
      (!isTeamHoliday(lead.agent) && isWithinBusinessHours(lead.agent)));
  const disableCall =
    !lead?.phone ||
    !hasCallAbility ||
    (isCallCenterRoutingEnabled && !canMakeCallCenterCall) ||
    isDialing ||
    isFetchingCallToken;

  const classes = useStyles({
    isOnCall,
    isReceivingInboundCall,
    hasCallAbility,
    isCallCenterRoutingEnabled,
  });

  const playInboundRinging = () => {
    if (!inboundRingingRef?.current) {
      inboundRingingRef.current = new Audio(inboundRingingSrc);
    }

    inboundRingingRef.current.play();
  };

  const stopInboundRinging = () => {
    if (inboundRingingRef?.current) {
      inboundRingingRef.current.pause();
      inboundRingingRef.current.currentTime = 0;
    }
  };

  const playOutboundRinging = () => {
    if (!outboundRingingRef?.current) {
      outboundRingingRef.current = new Audio(outboundRingingSrc);
    }

    outboundRingingRef.current.play();
  };

  const stopOutboundRinging = () => {
    if (outboundRingingRef?.current) {
      outboundRingingRef.current.pause();
      outboundRingingRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    if (outboundRinging) {
      playOutboundRinging();
    } else {
      stopOutboundRinging();
    }
  }, [outboundRinging]);

  const callLead = () => {
    if (isCallCenterRoutingEnabled) {
      if (canMakeCallCenterCall) {
        setIsDialing(true);
        dispatch(apiInitiateCallCenterFlow({ leadId: lead.id }));
      }

      return;
    }

    if (lead?.communicationProvider && concierge) {
      if (!lead?.phone || !isValidPhoneNumber(lead.phone, lead.phoneCountryIso || 'US')) {
        dispatch(
          setNotification({
            type: 'error',
            text: AppCopyText.INVALID_PHONE,
          })
        );
        return;
      }

      const communicationService = getCommunicationService(lead.communicationProvider);
      communicationService.dialOutbound(lead, concierge);
      if (communicationService.needsRing()) {
        dispatch(setOutboundRinging(true));
      }
    }
  };

  const answerCall = () => {
    if (!lead?.communicationProvider || !concierge) return;

    stopInboundRinging();
    setIsAnswering(true);

    getCommunicationService(lead.communicationProvider).acceptInbound(lead, concierge);
  };

  const hotKeyHandlers = {
    CALL: callLead,
  };

  // ensure call is not active after this component is unmounted
  useEffect(() => {
    return () => {
      if (!lead?.communicationProvider) return;

      getCommunicationService(lead.communicationProvider).endCall();
    };
  }, []);

  useEffect(() => {
    if (isReceivingInboundCall && (navigator as any).userActivation.hasBeenActive) {
      playInboundRinging();
    } else {
      stopInboundRinging();
    }
  }, [isReceivingInboundCall]);

  return (
    <div>
      <ActionsPanel />
      <Paper className={classes.callPanelWrapper} elevation={0}>
        {showGreeting && <InboundCallGreeting />}
        {!isEngaged && (
          <>
            <CallLead
              onClick={callLead}
              lead={leadName}
              disable={disableCall}
              isCallCenterRoutingEnabled={isCallCenterRoutingEnabled}
            />
            <GlobalHotKeys handlers={hotKeyHandlers} />
          </>
        )}
        {isReceivingInboundCall && (
          <AnswerCall
            onClick={answerCall}
            disabled={isAnswering || isFetchingCallToken}
            label={isAnswering ? 'Connecting...' : 'Answer Call'}
          />
        )}
        {isOnCall && <OnCall />}
      </Paper>
    </div>
  );
};

export default LeadActionsPanel;
